import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, HeartPulse, AlertTriangle, AlertCircle, Info, CheckCircle2, RefreshCw, Send } from "lucide-react";
import {
  listMaintenanceAlerts,
  getHealthSnapshot,
  resolveAlert,
  reopenAlert,
  resendAlert,
  triggerTestAlert,
} from "@/lib/maintenance/alerts.functions";

export const Route = createFileRoute("/_authenticated/admin/health")({
  head: () => ({ meta: [{ title: "Health & Alerts — Admin" }] }),
  component: HealthPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-16 text-center">
        <p className="text-red-400 mb-3 text-sm">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="text-zinc-400">Not found.</div>,
});

function HealthPage() {
  const snapFn = useServerFn(getHealthSnapshot);
  const listFn = useServerFn(listMaintenanceAlerts);
  const resolveFn = useServerFn(resolveAlert);
  const reopenFn = useServerFn(reopenAlert);
  const resendFn = useServerFn(resendAlert);
  const testFn = useServerFn(triggerTestAlert);
  const qc = useQueryClient();

  const [tab, setTab] = useState<"open" | "resolved">("open");

  const snap = useQuery({ queryKey: ["health", "snap"], queryFn: () => snapFn() });
  const alerts = useQuery({
    queryKey: ["health", "alerts", tab],
    queryFn: () => listFn({ data: { resolved: tab === "resolved", limit: 200 } }),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["health"] });
  };

  const resolveMut = useMutation({ mutationFn: (id: string) => resolveFn({ data: { id } }), onSuccess: invalidate });
  const reopenMut = useMutation({ mutationFn: (id: string) => reopenFn({ data: { id } }), onSuccess: invalidate });
  const resendMut = useMutation({ mutationFn: (id: string) => resendFn({ data: { id } }), onSuccess: invalidate });
  const testMut = useMutation({ mutationFn: () => testFn(), onSuccess: invalidate });

  const counts = snap.data?.counts;
  const services = snap.data?.services ?? [];

  return (
    <div className="space-y-6 max-w-6xl">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase inline-flex items-center gap-1.5">
            <HeartPulse className="size-3.5" /> System Health
          </p>
          <h1 className="font-serif text-3xl text-white mt-1">Health & Maintenance Alerts</h1>
          <p className="text-zinc-500 text-sm mt-1">Live status of backend services and any developer alerts raised in the last 24h.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => invalidate()} className="px-4 py-2 text-xs rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 inline-flex items-center gap-1.5">
            <RefreshCw className="size-3.5" /> Refresh
          </button>
          <button onClick={() => testMut.mutate()} disabled={testMut.isPending} className="px-4 py-2 text-xs rounded-full bg-amber-300 text-zinc-950 font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
            <Send className="size-3.5" /> Send test alert
          </button>
        </div>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Open alerts" value={counts?.open_total ?? "—"} accent="amber" />
        <Kpi label="Critical" value={counts?.open_critical ?? "—"} accent="red" />
        <Kpi label="AI failures (24h)" value={counts?.ai_failures_24h ?? "—"} accent="amber" />
        <Kpi label="Estimates (24h)" value={counts?.estimates_24h ?? "—"} accent="emerald" />
      </div>

      {/* Service status */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <h2 className="text-sm font-semibold text-white mb-3">Service status</h2>
        {snap.isLoading ? (
          <Loader2 className="size-4 animate-spin text-zinc-500" />
        ) : (
          <div className="grid sm:grid-cols-2 gap-2">
            {services.map((s: any) => (
              <div key={s.name} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/5">
                <StatusDot status={s.status} />
                <div className="min-w-0">
                  <div className="text-sm text-white">{s.name}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{s.detail}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Alerts */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
          <button onClick={() => setTab("open")} className={`px-3 py-1.5 text-xs rounded-full ${tab === "open" ? "bg-amber-300 text-zinc-950 font-semibold" : "text-zinc-400 hover:text-white"}`}>Active</button>
          <button onClick={() => setTab("resolved")} className={`px-3 py-1.5 text-xs rounded-full ${tab === "resolved" ? "bg-amber-300 text-zinc-950 font-semibold" : "text-zinc-400 hover:text-white"}`}>Resolved</button>
        </div>
        {alerts.isLoading ? (
          <div className="py-16 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div>
        ) : (alerts.data ?? []).length === 0 ? (
          <div className="py-16 text-center text-zinc-500 text-sm">{tab === "open" ? "No active alerts. All systems normal." : "No resolved alerts yet."}</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {(alerts.data ?? []).map((a: any) => (
              <li key={a.id} className="px-5 py-4 flex flex-col md:flex-row md:items-start gap-3 md:gap-4">
                <SeverityBadge severity={a.severity} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-sm text-white font-medium">{a.source}</span>
                    {a.route && <code className="text-[11px] text-zinc-500">{a.route}</code>}
                    <span className="text-[11px] text-zinc-500">×{a.occurrence_count}</span>
                  </div>
                  <p className="text-sm text-zinc-300 mt-1 whitespace-pre-wrap break-words">{a.message}</p>
                  <div className="text-[11px] text-zinc-500 mt-1.5">
                    Last seen {new Date(a.last_seen_at).toLocaleString()}{a.last_emailed_at ? ` · Last email ${new Date(a.last_emailed_at).toLocaleString()}` : " · No email sent"}
                  </div>
                  {a.metadata?.suggestion && <p className="text-[12px] text-amber-300/90 mt-2">→ {String(a.metadata.suggestion)}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  {tab === "open" ? (
                    <>
                      <button onClick={() => resendMut.mutate(a.id)} disabled={resendMut.isPending} className="px-3 py-1.5 text-xs rounded-full border border-white/10 text-zinc-300 hover:bg-white/5">Resend</button>
                      <button onClick={() => resolveMut.mutate(a.id)} disabled={resolveMut.isPending} className="px-3 py-1.5 text-xs rounded-full bg-emerald-400/20 border border-emerald-400/30 text-emerald-200">Resolve</button>
                    </>
                  ) : (
                    <button onClick={() => reopenMut.mutate(a.id)} disabled={reopenMut.isPending} className="px-3 py-1.5 text-xs rounded-full border border-white/10 text-zinc-300 hover:bg-white/5">Reopen</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Kpi({ label, value, accent }: { label: string; value: any; accent: "amber" | "red" | "emerald" }) {
  const cls = accent === "red" ? "text-red-300" : accent === "emerald" ? "text-emerald-300" : "text-amber-300";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-2xl font-serif mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const cls = status === "ok" ? "bg-emerald-400" : status === "warn" ? "bg-amber-400" : "bg-red-500";
  return <span className={`size-2.5 rounded-full ${cls} shrink-0`} />;
}

function SeverityBadge({ severity }: { severity: string }) {
  const map = {
    critical: { Icon: AlertCircle, cls: "bg-red-500/15 text-red-300 border-red-500/30" },
    warning: { Icon: AlertTriangle, cls: "bg-amber-400/15 text-amber-200 border-amber-400/30" },
    info: { Icon: Info, cls: "bg-blue-400/15 text-blue-200 border-blue-400/30" },
  } as const;
  const { Icon, cls } = (map as any)[severity] ?? { Icon: CheckCircle2, cls: "bg-white/5 text-zinc-300 border-white/10" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${cls} h-fit`}>
      <Icon className="size-3" /> {severity}
    </span>
  );
}